import { useRef } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import makeStyles from '@mui/styles/makeStyles'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import { datasetPhases, colors } from '../../global/constants'
import { t } from '@lingui/core/macro'

const chartHeight = 80
const useStyles = makeStyles(theme => ({
  datapointLabel: {
    fontWeight: 'bold',
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  noData: {
    fontStyle: 'italic',
  },
  outboundContainer: {
    alignItems: 'center',
    fontSize: '12px',
  },
  chartContainer: {
    height: `${chartHeight}px`,
  },
  outboundIcon: {
    fontSize: '60px',
  },
  outboundCaption: {
    fontStyle: 'italic',
  },
  verticalDivider: {
    height: '100px',
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    borderLeft: '1px solid #ddd',
  },
}))

const summaryPropToLabelMap = {
  pendingCount: `Pending`,
  completeCount: `Success`,
  errorCount: `Failure`,
  lastChecked: `Last Checked`,
}
const keys = [
  summaryPropToLabelMap.pendingCount,
  summaryPropToLabelMap.completeCount,
  summaryPropToLabelMap.errorCount,
]
const labelToColorMap = {
  [summaryPropToLabelMap.pendingCount]: colors.gray.light,
  [summaryPropToLabelMap.completeCount]: colors.green.main,
  [summaryPropToLabelMap.errorCount]: colors.peach.main,
}

function CustomChartTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    return (
      <>
        <span>{`${payload[0].name}: `}</span>
        <span style={{ fontWeight: 'bold' }}>{payload[0].value}</span>
      </>
    )
  }
  return null
}

export const SubmissionStatusOutbound = ({ submission, submissionSummary }) => {
  const classes = useStyles()
  const chartContainerRef = useRef(null)
  const history = useHistory()
  const showMoreDetails = !history.location.pathname.includes('history')

  if (!submissionSummary || !submissionSummary.datasetSubmissionId) {
    return (
      <div className={classes.outboundContainer}>
        <div className={classes.noData}>{t`Waiting for submission summary data.`}</div>
      </div>
    )
  }

  const submissionSummaryWithLabelProps = {
    id: submissionSummary.datasetSubmissionId.toString(),
    [summaryPropToLabelMap.pendingCount]: submissionSummary.pendingCount,
    [summaryPropToLabelMap.completeCount]: submissionSummary.completeCount,
    [summaryPropToLabelMap.errorCount]: submissionSummary.errorCount,
    [summaryPropToLabelMap.lastChecked]: submissionSummary.lastChecked,
  }

  const data = [submissionSummaryWithLabelProps]

  function navToSubmission(event) {
    event.preventDefault()
    history.push(`/codes/details/${submission.dataset_id}/history`)
  }

  return (
    <div className={classes.outboundContainer}>
      <h1>{t`submission-status-outbound`}</h1>
      <div className={classes.chartContainer} ref={chartContainerRef}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 15, bottom: -15, left: 0, right: 0 }}
          >
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="id" hide />
            <Tooltip content={<CustomChartTooltip />} />
            {keys.map(key => (
              <Bar key={key} dataKey={key} fill={labelToColorMap[key]} stackId="stack" />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className={classes.footerContainer}>
        {submission.phase !== datasetPhases.COMPLETED ? (
          <span>
            {t`Last checked:`}
            <span className={classes.datapointLabel}>
              {` ${moment(submissionSummary.lastChecked).format('M/D/YYYY h:mm:ss a')}`}
            </span>
          </span>
        ) : (
          <span>
            {`Completed ${submissionSummary.errorCount > 0 ? 'with errors at' : 'at'}`}:
            <span className={classes.datapointLabel}>
              {` ${moment(submission.completed_at).format('M/D/YYYY h:mm:ss a')}`}
            </span>
          </span>
        )}
        {showMoreDetails && (
          <a onClick={navToSubmission} style={{ cursor: 'pointer' }}>
            <InfoOutlinedIcon
              style={{ verticalAlign: '-4px', marginRight: '3px', fontSize: '17px' }}
            />
            <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>MORE DETAILS</span>
          </a>
        )}
      </div>
    </div>
  )
}
